/**
 * Local harness catalogue injected by the desktop shell (no World round-trip).
 *
 * Presence remains the source of truth for phones and other windows. On desktop
 * the shell already knows which CLIs are on this machine, so it publishes them
 * here immediately — the composer can offer Claude/Codex/Cursor before the
 * first presence beat syncs.
 */

import type { HarnessPresence } from '@mod/core/model'
import { useEffect, useState } from 'react'

/** Where the shell writes the latest probe. Same pattern as `__modAgentView`. */
export const LOCAL_HARNESSES_GLOBAL = '__modLocalHarnesses'
/** Dispatched after each publish so React can re-render without polling. */
export const LOCAL_HARNESSES_EVENT = 'mod:local-harnesses'

function readGlobal(): HarnessPresence[] {
  try {
    const raw = (globalThis as unknown as Record<string, unknown>)[
      LOCAL_HARNESSES_GLOBAL
    ]
    return Array.isArray(raw) ? (raw as HarnessPresence[]) : []
  } catch {
    return []
  }
}

/** Connected driveable harnesses on this machine, or [] outside the desktop shell. */
export function useLocalHarnesses(): HarnessPresence[] {
  const [harnesses, setHarnesses] = useState<HarnessPresence[]>(readGlobal)

  useEffect(() => {
    const onPublish = (e: Event) => {
      const detail = (e as CustomEvent<unknown>).detail
      if (Array.isArray(detail)) setHarnesses(detail as HarnessPresence[])
      else setHarnesses(readGlobal())
    }
    window.addEventListener(LOCAL_HARNESSES_EVENT, onPublish)
    // Shell may have published before this mount subscribed.
    setHarnesses(readGlobal())
    return () => window.removeEventListener(LOCAL_HARNESSES_EVENT, onPublish)
  }, [])

  return harnesses
}
